import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { MEDIA } from "@/data/media";
import { ProductCard } from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { breadcrumbSchema, collectionPageSchema } from "@/lib/seo";

const TITLES = {
  "all": "All",
  "new-in": "New In",
  "sale": "Sale",
  "necklace-sets": "Necklace Sets",
  "bridal-sets": "Bridal Sets",
  "bangles": "Bangles",
  "earrings": "Earrings",
  "maang-tikka": "Maang Tikka",
  "rings": "Rings",
  "nose-rings": "Nose Rings",
  "anklets": "Anklets",
  "hair-accessories": "Hair Accessories",
};

const SEO_META = {
  "all": ["Shop All - South Asian Imitation Jewellery", "Browse the full Zebaa collection - bridal sets, kundan-style necklaces, gold-tone bangles, chandbalis and jhumkas. Green Street, London & online with Royal Mail tracked UK delivery."],
  "new-in": ["New In - Latest South Asian Jewellery Arrivals", "Discover the newest imitation jewellery arrivals at Zebaa on Green Street, London - bridal sets, necklace sets, bangles and earrings, added regularly."],
  "sale": ["Sale - Imitation Jewellery Up to 50% Off", "Shop the Zebaa sale - bridal sets, necklace sets, gold-tone bangles and earrings at exceptional prices. Green Street, East London & online."],
  "necklace-sets": ["Necklace Sets - Kundan, Polki & Temple Style, London", "Shop kundan-style, polki-style and temple-style necklace sets at Zebaa on Green Street, East London. Imitation jewellery with UK delivery."],
  "bridal-sets": ["Bridal Sets - South Asian Bridal Jewellery, London", "Hand-embellished South Asian bridal jewellery sets at Zebaa on Green Street, East London. Kundan-style, polki-style and temple-style bridal designs."],
  "bangles": ["Bangles, Kadas & Chudi Stacks - Green Street, London", "Gold-tone bangles, kadas and chudi stacks at Zebaa on Green Street, East London. Imitation jewellery with fast Royal Mail tracked UK delivery."],
  "earrings": ["Earrings - Jhumkas, Chandbalis & Studs, London", "Shop jhumkas, chandbalis, drops and studs at Zebaa on Green Street, East London - South Asian imitation earrings with UK delivery."],
  "maang-tikka": ["Maang Tikka, Matha Patti & Passa - Green Street, London", "Kundan-style and polki-style maang tikkas, matha pattis and passas at Zebaa on Green Street, East London. Imitation bridal jewellery with UK delivery."],
  "rings": ["Rings & Haath Phool - Imitation Jewellery, London", "Statement cocktail rings, kundan-style rings and haath phool at Zebaa on Green Street, East London. Shop online with UK delivery."],
  "nose-rings": ["Nose Rings, Bridal Nath & Nose Pins - London", "Bridal naths, nose rings and nose pins at Zebaa on Green Street, East London. South Asian imitation jewellery with UK delivery."],
  "anklets": ["Anklets & Payal - Green Street, London", "Payal and anklets in gold-tone and oxidised finishes at Zebaa on Green Street, East London. UK delivery available."],
  "hair-accessories": ["Jhoomar, Paranda & Hair Jewellery - London", "Jhoomars, parandas and bridal hair accessories at Zebaa on Green Street, East London. Imitation jewellery with UK delivery."],
};

const SUBTITLES = {
  "all": "Bridal sets, necklaces, bangles and earrings - imitation jewellery.",
  "new-in": "The latest arrivals, straight from Jaipur and Hyderabad.",
  "sale": "Statement pieces at exceptional prices.",
  "necklace-sets": "Kundan-style, polki-style and temple designs.",
  "bridal-sets": "The statement - coordinated bridal jewellery.",
  "bangles": "Bangles, kadas and chudi stacks.",
  "earrings": "Jhumkas, chandbalis, drops and studs.",
  "maang-tikka": "Tikkas, matha pattis and passas.",
  "rings": "Cocktail rings and haath phool.",
  "nose-rings": "Bridal naths, nose rings and pins.",
  "anklets": "Payal and anklets.",
  "hair-accessories": "Jhoomars, parandas and hair pins.",
};

const BANNER_IMAGES = {
  "all": MEDIA.catJewellery,
  "new-in": MEDIA.catNewIn,
  "sale": MEDIA.catLehengas,
  "necklace-sets": MEDIA.catJewellery,
  "bridal-sets": MEDIA.catLehengas,
  "bangles": MEDIA.catSuits,
  "earrings": MEDIA.catSarees,
  "maang-tikka": MEDIA.catJewellery,
  "rings": MEDIA.catJewellery,
  "nose-rings": MEDIA.catJewellery,
  "anklets": MEDIA.catJewellery,
  "hair-accessories": MEDIA.catJewellery,
};

const CATEGORY_INTRO = {
  "necklace-sets": "Kundan-style, polki-style and temple necklace sets at Zebaa on Green Street, East London. Each necklace is set in a gold-tone alloy with faux pearls and coloured stones - designed to look the part on the big day without the price tag of fine jewellery. Order online with Royal Mail tracked UK delivery, or visit our Green Street shop near Upton Park and East Ham.",
  "bridal-sets": "Shop coordinated South Asian bridal sets at Zebaa, Green Street, East London - necklace, earrings and maang tikka pieces in matching kundan-style, polki-style and temple designs. Everything is imitation (artificial) jewellery, hand-picked from partner workshops in Jaipur and Hyderabad. Available online with UK delivery, or in-store on Green Street.",
  "bangles": "Explore gold-tone bangles, kadas and chudi stacks at Zebaa on Green Street near Upton Park. Meenakari, kundan-style and plain polished finishes for weddings, Eid and every celebration. Shop online with Royal Mail tracked UK delivery or visit us in East London.",
  "earrings": "Jhumkas, chandbalis, drops and studs at Zebaa - South Asian imitation earrings on Green Street, East London. Faux pearl drops, kundan-style, meenakari and temple-style pieces to complete your look. UK delivery via Royal Mail.",
  "new-in": "Browse the latest South Asian imitation jewellery arrivals at Zebaa. New bridal sets, necklace sets, bangles and earrings are added regularly - be the first to shop fresh pieces online with UK delivery or in our Green Street, East London shop.",
  "sale": "Shop the Zebaa sale - bridal sets, kundan-style necklaces, gold-tone bangles and chandbalis at exceptional prices. Limited-time reductions on selected imitation jewellery, available online with UK delivery and in-store on Green Street, East London.",
  "all": "Browse the complete Zebaa collection of South Asian imitation jewellery - bridal sets, kundan-style necklaces, polki-style chokers, temple pieces, gold-tone bangles, jhumkas and chandbalis. Hand-picked from partner workshops in Jaipur and Hyderabad. Shop online with UK delivery or visit us in-store on Green Street, East London near Upton Park and East Ham.",
};


export default function Shop() {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const [products, setProducts] = useState([]);
  const [sort, setSort] = useState("featured");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category === "new-in") params.new_in = true;
    else if (category === "sale") params.on_sale = true;
    else if (category && category !== "all") params.category = category;
    api.get("/products", { params }).then(({ data }) => {
      setProducts(data);
      setLoading(false);
    });
  }, [category]);

  const filtered = useMemo(() => {
    let list = [...products];
    if (q) {
      const s = q.toLowerCase();
      list = list.filter(
        (p) => p.name.toLowerCase().includes(s) || p.subtitle.toLowerCase().includes(s) || p.category.includes(s)
      );
    }
    if (sort === "price-low") list.sort((a, b) => a.price - b.price);
    else if (sort === "price-high") list.sort((a, b) => b.price - a.price);
    return list;
  }, [products, q, sort]);

  const title = q ? `Search: "${q}"` : TITLES[category] || "Collections";
  const [seoTitle, seoDesc] = SEO_META[category] || [null, null];

  return (
    <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="shop-page">
      {!q && seoTitle && (
        <Seo
          title={seoTitle}
          description={seoDesc}
          path={`/shop/${category}`}
          jsonLd={[
            breadcrumbSchema([
              { name: "Home", path: "/" },
              { name: TITLES[category] || "Shop", path: `/shop/${category}` },
            ]),
            collectionPageSchema({
              name: seoTitle,
              description: seoDesc,
              path: `/shop/${category}`,
              products: filtered,
            }),
          ]}
        />
      )}
      {q && <Seo title={`Search: ${q}`} path={`/shop/${category}`} noindex />}
      <Reveal className="mb-10">
        <div className="relative overflow-hidden rounded-lg bg-[#141210] min-h-[220px] lg:min-h-[300px] flex items-center" data-testid="shop-hero-banner">
          <img
            src={MEDIA.hero}
            alt={`${title} at Zebaa`}
            loading="lazy"
            className="pointer-events-none select-none absolute left-0 bottom-0 h-full w-auto max-w-[62%] object-contain object-left-bottom hidden sm:block"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to left, #141210 0%, #141210 34%, rgba(20,18,16,0.55) 52%, rgba(20,18,16,0) 78%)" }} />
          <div className="relative z-10 px-7 sm:px-10 lg:px-16 py-12 lg:py-16 w-full flex flex-col items-start text-left sm:items-end sm:text-right">
            <h1 className="font-sans font-extrabold uppercase tracking-tight text-4xl sm:text-5xl lg:text-7xl leading-[0.92] text-pink">{title}</h1>
            <p className="text-white/60 mt-4 text-sm lg:text-base max-w-md">{q ? "" : SUBTITLES[category] || ""}</p>
            <div className="w-16 h-px bg-metallic-gold mt-6" />
            {/* mobile - small brand line under the divider */}
            <div className="sm:hidden mt-7">
              <p
                className="font-sans font-semibold tracking-tight text-2xl"
                style={{
                  backgroundImage: "linear-gradient(90deg, #C9A24B 0%, #E4C87F 50%, #EAD8A0 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                Bridal &amp; Everyday
              </p>
              <p className="font-sans text-xs text-white/50 mt-1.5">
                South Asian imitation jewellery from Green Street
              </p>
            </div>
          </div>
          <div className="hidden sm:flex absolute inset-0 z-10 flex-col items-center justify-center text-center px-4 pointer-events-none">
            <p
              className="font-sans font-semibold tracking-tight text-2xl sm:text-3xl lg:text-4xl"
              style={{
                backgroundImage: "linear-gradient(90deg, #C9A24B 0%, #E4C87F 50%, #EAD8A0 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              Bridal &amp; Everyday
            </p>
            <p className="font-sans text-xs sm:text-sm text-white/50 mt-2.5">
              South Asian imitation jewellery from Green Street
            </p>
          </div>
        </div>
      </Reveal>

      <div className="flex items-center justify-between mb-8 border-b border-gray-100 pb-4">
        <span className="text-sm text-gray-500" data-testid="results-count">{filtered.length} items</span>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          data-testid="sort-select"
          className="text-xs uppercase tracking-[0.12em] border border-gray-200 px-3 py-2 focus:outline-none focus:border-pink"
        >
          <option value="featured">Sort: Featured</option>
          <option value="price-low">Price: Low to High</option>
          <option value="price-high">Price: High to Low</option>
        </select>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12 animate-pulse" data-testid="shop-loading">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex flex-col">
              <div className="aspect-[3/4] bg-surface" />
              <div className="h-4 w-2/3 bg-surface mt-4 mx-auto" />
              <div className="h-3 w-1/3 bg-surface mt-2 mx-auto" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-center text-gray-400 py-20" data-testid="no-results">No products found.</p>
      ) : (
        <div key={`${category}-${sort}-${q}`} className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
          {filtered.map((p, i) => (
            <Reveal key={p.id} delay={(i % 4) * 0.06}>
              <ProductCard product={p} index={i} />
            </Reveal>
          ))}
        </div>
      )}

      {!q && CATEGORY_INTRO[category] && (
        <Reveal className="mt-16 pt-10 border-t border-gray-100 max-w-3xl">
          <h2 className="font-serif text-xl text-charcoal">{seoTitle}</h2>
          <p className="text-gray-600 leading-relaxed mt-4 text-[15px]" data-testid="category-seo-intro">
            {CATEGORY_INTRO[category]}
          </p>
        </Reveal>
      )}
    </div>
  );
}
